import React from 'react';
import {Field, Form, Formik} from "formik";
import * as Yup from "yup";
import TextFieldWithErrorMessage from "../form/TextFieldWithErrorMessage";
import {Button, Grid, Typography} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {register} from "../../api/AuthenticationEndpoints";
import {RR_STATUS} from "../../constants/RRConstants";
import {useSnackbar} from "notistack";
import {TFunction} from "i18next";
import Loader from "../core/Loader";
import ComboboxWithErrorMessage from "../form/ComboboxWithErrorMessage";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";

const MAX_LIMIT_KEY = "form.registration.error.max-limit";
const REQUIRED_KEY = "form.registration.error.required";
const PHONE_REGEX = "^[0-9, ,/,\\-,+,(,)]*$";

const validationSchema = Yup.object({
    account: Yup.string()
        .matches("^[0-9]*$", "form.registration.error.not-a-number")
        .max(100, MAX_LIMIT_KEY)
        .required(REQUIRED_KEY, true),
    partyName: Yup.string()
        .max(100, MAX_LIMIT_KEY)
        .required(REQUIRED_KEY, true),
    salutation: Yup.string()
        .required(REQUIRED_KEY, true),
    firstName: Yup.string()
        .max(100, MAX_LIMIT_KEY)
        .required(REQUIRED_KEY, true),
    lastName: Yup.string()
        .max(100, MAX_LIMIT_KEY)
        .required(REQUIRED_KEY, true),
    userName: Yup.string()
        .max(100, MAX_LIMIT_KEY)
        .required(REQUIRED_KEY, true),
    email: Yup.string()
        .max(100, MAX_LIMIT_KEY)
        .email("form.registration.error.email")
        .required(REQUIRED_KEY, true),
    contactType: Yup.string()
        .required(REQUIRED_KEY, true),
    phone: Yup.string()
        .matches(PHONE_REGEX, "form.registration.error.invalid-phone-number")
        .max(100, MAX_LIMIT_KEY),
    mobile: Yup.string()
        .matches(PHONE_REGEX, "form.registration.error.invalid-phone-number")
        .max(100, MAX_LIMIT_KEY),
    comment: Yup.string()
        .max(1000, "form.registration.error.max-limit-comment")
});

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        button: {
            width: 150
        }
    }),
);

interface IUser {
    account?: string,
    firstName?: string,
    lastName?: string,
    userName?: string,
    partyName?: string,
    salutation?: string,
    contactType?: string,
    email?: string,
    phone?: string,
    mobile?: string,
    comment?: string
}

const Salutation = {
    MR: "form.registration.mr",
    MS: "form.registration.ms"
}

const ContactTypes = {
    PERSON: "form.registration.person",
    DEPARTMENT: "form.registration.department"
}


const createField = (name: string, required?: boolean) => {
    return <Field component={TextFieldWithErrorMessage} name={name} caption={"form.registration." + name}
                  required={required} variant={"outlined"} size={"small"}/>;
};

const createDropdown = <T extends object, >(name: string, enumType: T, t: TFunction, required?: boolean) => {
    return <Field component={ComboboxWithErrorMessage} name={name} caption={"form.registration." + name}
                  required={required} variant={"outlined"} size={"small"}>
        <option aria-label="None" value={""}/>
        {Object.keys(enumType).map(option => (
            <option key={option} value={option}>
                {t<string>(enumType[option])}
            </option>
        ))}
    </Field>
};

type RegistrationFormProps = {
    onSuccess: () => void
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({onSuccess}) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const classes = useStyles();

    const registerUser = (values: IUser): Promise<void> => {

        return register(values).then(({data}) => {
            const {status, message} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("form.registration.error.registration-failed") + message, {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                onSuccess();
            }
        });
    };

    return (
        <Formik initialValues={{
            account: "",
            firstName: "",
            lastName: "",
            userName: "",
            partyName: "",
            salutation: "",
            contactType: "",
            email: "",
            phone: "",
            mobile: "",
            comment: ""
        }}
                validationSchema={validationSchema}
                onSubmit={registerUser}>
            {({isSubmitting}) => (
                <Form>
                    <Grid container spacing={2}>
                        <Grid item sm={12} md={6}>
                            {createField("account", true)}
                            {createField("partyName", true)}
                            {createDropdown("salutation", Salutation, t, true)}
                            {createField("firstName", true)}
                            {createField("lastName", true)}
                            {createField("userName", true)}
                            {createField("email", true)}
                        </Grid>
                        <Grid item sm={12} md={6}>
                            {createDropdown("contactType", ContactTypes, t, true)}
                            {createField("phone")}
                            {createField("mobile")}
                            <Field component={TextFieldWithErrorMessage} name={"comment"}
                                   caption={"form.registration.comment"} variant={"outlined"} size={"small"} multiline
                                   rows={4}/>
                            <Typography>{t("form.registration.requiredMessage")}</Typography>
                        </Grid>
                        <Grid container item justifyContent={"space-evenly"}>
                            {isSubmitting ? <Loader/> :
                                <>
                                    <Button className={classes.button} variant={"contained"} onClick={() => {
                                        navigate(-1);
                                    }}>{t("core.close")}</Button>
                                    <Button className={classes.button} variant={"contained"} color={"primary"}
                                            type="submit">{t("buttons.submit")}</Button>
                                </>}
                        </Grid>
                    </Grid>
                </Form>
            )}

        </Formik>
    );
};

export default RegistrationForm;
